import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, output, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { IconComponent } from '@outlaw/icons';
import { DragAndDropDirective } from '../../../directives/drag-and-drop.directive';
import { SnackbarService } from '../../../services/util/snackbar.service';
import { StreamComponent } from '../../stream/stream.component';
import { FloatingInputComponent } from '../../floating-input/floating-input.component';
import { FloatingTextareaComponent } from '../../floating-textarea/floating-textarea.component';
import { RingLoaderComponent } from '../../ring-loader/ring-loader.component';
import { SelectionsMenuComponent } from '../../selections-menu/selections-menu.component';
import { AddPostStateService } from '../add-post-state.service';

const MAX_VIDEO_SIZE = 500 * 1024 * 1024;
const ACCEPTED_VIDEO_EXTENSIONS = ['.mp4', '.mov', '.webm', '.avi'];

@Component({
  selector: 'outlaw-video-tab',
  templateUrl: './video-tab.component.html',
  styleUrls: ['./video-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, IconComponent, DragAndDropDirective, StreamComponent, FloatingInputComponent, FloatingTextareaComponent, RingLoaderComponent, SelectionsMenuComponent],
})
export class VideoTabComponent {
  @ViewChild('videoInput') videoInput?: ElementRef<HTMLInputElement>;
  @ViewChild('thumbnailInput') thumbnailInput?: ElementRef<HTMLInputElement>;
  @ViewChild('videoStream') videoStream?: StreamComponent;

  state = inject(AddPostStateService);
  #snackbar = inject(SnackbarService);

  form = input.required<FormGroup<{
    title: FormControl<string>;
    description: FormControl<string>;
  }>>();

  publishRequested = output<void>();
  isPlaying = signal(false);

  canPublish = computed(() =>
    this.state.videoUploadStatus() === 'ready' && !!this.state.videoStreamUrl() && !this.state.isUploadingThumbnail()
  );

  togglePlay(): void {
    if (this.isPlaying()) {
      this.videoStream?.pauseVideo();
      this.isPlaying.set(false);
    } else {
      this.videoStream?.playVideo();
      this.isPlaying.set(true);
    }
  }

  onFileDrop(files: FileList): void {
    this.#handleFile(files[0]);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.#handleFile(input.files[0]);
      input.value = '';
    }
  }

  onDropZoneClick(): void {
    this.videoInput?.nativeElement?.click();
  }

  onThumbnailClick(): void {
    this.thumbnailInput?.nativeElement?.click();
  }

  onThumbnailSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    if (!file.type.startsWith('image/')) {
      this.#snackbar.show({ message: 'Please select an image file' });
      return;
    }
    input.value = '';
    this.state.uploadThumbnail(file);
  }

  removeThumbnail(): void {
    this.state.removeThumbnail();
  }

  cancelUpload(): void {
    this.state.cancelVideo();
  }

  retryUpload(): void {
    this.cancelUpload();
    this.onDropZoneClick();
  }

  #handleFile(file: File): void {
    const ext = file.name.includes('.') ? '.' + file.name.split('.').pop()?.toLowerCase() : '';
    const isVideoType = file.type.startsWith('video/');
    const isVideoExt = ACCEPTED_VIDEO_EXTENSIONS.includes(ext);
    if (!isVideoType && !isVideoExt) {
      this.#snackbar.show({ message: 'Unsupported video format. Use MP4, MOV, WebM, or AVI.' });
      return;
    }
    if (file.size > MAX_VIDEO_SIZE) {
      this.#snackbar.show({ message: 'Video file exceeds 500 MB limit' });
      return;
    }
    this.state.uploadVideo(file);
  }
}
